import { ALL_CHARACTERS } from "../types/character";
import type { HorrorRaceState } from "../types/game-state";
import type { Player } from "../types/player";

function resetForNewRace(players: readonly Player[]): readonly Player[] {
  return players.map((p) => ({
    ...p,
    hand: [...ALL_CHARACTERS],
    secretSelection: null,
    hasRevealed: false,
    hasConfirmedRecap: false,
    hasConfirmedRoundRecap: false,
  }));
}

// Official: the next race's round-1 start player is the one with the fewest
// garnets (ties → lowest seat index; the RPS tie-break is abstracted to be
// deterministic). Returns the seating order rotated to start there.
function nextRaceTurnOrder(
  players: readonly Player[],
  garnets: Readonly<Record<string, number>>,
): readonly number[] {
  let startIdx = 0;
  let min = Number.POSITIVE_INFINITY;
  for (let i = 0; i < players.length; i++) {
    const g = garnets[players[i].id] ?? 0;
    if (g < min) {
      min = g;
      startIdx = i;
    }
  }
  return players.map((_, k) => (startIdx + k) % players.length);
}

/**
 * T18: advance past the race-end recap. The tally (rankings/garnets/RaceResult)
 * was already computed and stored when the race finished (RACE_SCORING entry,
 * see applyResolveRound + computeRaceRecap); this applier does NOT recompute —
 * it only performs the transition the host submits once every player has
 * confirmed the recap. Either ends the game (last race) or resets every player +
 * the board for the next race, rotating the start player to the lowest-garnet
 * seat off the already-merged totals.
 */
export function applyAdvanceRace(state: HorrorRaceState): HorrorRaceState {
  const isLastRace = state.raceNumber >= state.config.totalRaces;

  if (isLastRace) {
    return { ...state, phase: "GAME_OVER" };
  }

  return {
    ...state,
    raceNumber: state.raceNumber + 1,
    round: 1,
    players: resetForNewRace(state.players),
    characterPositions: {
      VAMPIRE: 0,
      JIANGSHI: 0,
      GUMIHO: 0,
      MUMMY: 0,
      ZOMBIE: 0,
    },
    coinPool: [],
    drawnCoins: [],
    zones: { 1: [], 2: [], 4: [] },
    turnOrder: nextRaceTurnOrder(state.players, state.garnets),
    currentTurnIndex: 0,
    drawPass: 1,
    forfeitPlayerIndex: null,
    hasForfeitThisRound: false,
    selectionsComplete: 0,
    phase: "SETUP_CHARACTER_SELECTION",
    roundResolution: null,
    // M1: each race reshuffles the pool + drops finished characters, so prior
    // race draw counts no longer map onto the new pool. Reset history to [].
    currentRaceDrawHistory: [],
  };
}
